/**
 * Team Collaboration Analytics Component
 * Phase 2: Team & Department Analytics
 */

import React from 'react';
import { 
  Users, 
  TrendingUp, 
  Target, 
  Share2,
  BarChart3,
  Activity,
  Brain,
  Network
} from 'lucide-react';
import { TeamCollaboration as TeamCollaborationType, CrossDepartmentInsights } from '../../services/analytics';

interface TeamCollaborationProps {
  data: TeamCollaborationType;
  crossDepartmentInsights: CrossDepartmentInsights;
  loading?: boolean;
}

export const TeamCollaboration: React.FC<TeamCollaborationProps> = ({ 
  data, 
  crossDepartmentInsights,
  loading = false 
}) => {
  if (loading) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="animate-pulse">
            <div className="h-6 bg-gray-200 rounded w-1/4 mb-4"></div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="h-24 bg-gray-200 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const formatNumber = (value: number | string | null | undefined, decimals: number = 1): string => {
    if (value === null || value === undefined) return '0';
    const num = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(num)) return '0';
    return num.toFixed(decimals);
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-blue-600';
    if (score >= 40) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreBadge = (score: number) => {
    if (score >= 80) return { text: 'Excellent', color: 'bg-green-100 text-green-800' };
    if (score >= 60) return { text: 'Good', color: 'bg-blue-100 text-blue-800' };
    if (score >= 40) return { text: 'Average', color: 'bg-yellow-100 text-yellow-800' };
    return { text: 'Needs Improvement', color: 'bg-red-100 text-red-800' };
  };

  return (
    <div className="space-y-6">
      {/* Collaboration Metrics Overview */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">Team Collaboration Metrics</h3>
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <Network className="h-4 w-4" />
            <span>Cross-department collaboration</span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Cross-Department Participation */}
          <div className="text-center">
            <div className="flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mx-auto mb-4">
              <Users className="h-8 w-8 text-blue-600" />
            </div>
            <h4 className="text-lg font-semibold text-gray-900 mb-2">Cross-Department Participation</h4>
            <div className={`text-3xl font-bold mb-2 ${getScoreColor(data.cross_department_participation)}`}>
              {formatNumber(data.cross_department_participation)}%
            </div>
            <div className="text-sm text-gray-600">
              {crossDepartmentInsights.total_departments_involved} departments involved
            </div>
          </div>

          {/* Collaboration Score */}
          <div className="text-center">
            <div className="flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mx-auto mb-4">
              <Target className="h-8 w-8 text-green-600" />
            </div>
            <h4 className="text-lg font-semibold text-gray-900 mb-2">Collaboration Score</h4>
            <div className={`text-3xl font-bold mb-2 ${getScoreColor(data.collaboration_score)}`}>
              {formatNumber(data.collaboration_score)}%
            </div>
            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getScoreBadge(data.collaboration_score).color}`}>
              {getScoreBadge(data.collaboration_score).text}
            </span>
          </div>

          {/* Team Diversity */}
          <div className="text-center">
            <div className="flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mx-auto mb-4">
              <Brain className="h-8 w-8 text-purple-600" />
            </div>
            <h4 className="text-lg font-semibold text-gray-900 mb-2">Team Diversity Score</h4>
            <div className={`text-3xl font-bold mb-2 ${getScoreColor(data.team_diversity_score)}`}>
              {formatNumber(data.team_diversity_score)}%
            </div>
            <div className="text-sm text-gray-600">
              Department involvement
            </div>
          </div>
        </div>
      </div>

      {/* Detailed Collaboration Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Team Size & Development */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h4 className="text-lg font-semibold text-gray-900 mb-4">Team Development</h4>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Users className="h-5 w-5 text-blue-600" />
                <span className="text-sm font-medium text-gray-700">Average Team Size</span>
              </div>
              <span className="text-lg font-semibold text-gray-900">
                {formatNumber(data.average_team_size)} members
              </span>
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <TrendingUp className="h-5 w-5 text-green-600" />
                <span className="text-sm font-medium text-gray-700">Mentee Development Rate</span>
              </div>
              <span className="text-lg font-semibold text-gray-900">
                {formatNumber(data.mentee_development_rate)}%
              </span>
            </div>
            
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-green-500 h-2 rounded-full"
                style={{ width: `${Math.min(data.mentee_development_rate, 100)}%` }}
              ></div>
            </div>
          </div>
        </div>

        {/* Knowledge Sharing */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h4 className="text-lg font-semibold text-gray-900 mb-4">Knowledge Sharing</h4>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Share2 className="h-5 w-5 text-purple-600" />
                <span className="text-sm font-medium text-gray-700">Knowledge Sharing Index</span>
              </div>
              <span className="text-lg font-semibold text-gray-900">
                {formatNumber(data.knowledge_sharing_index)}%
              </span>
            </div>
            
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-purple-500 h-2 rounded-full"
                style={{ width: `${Math.min(data.knowledge_sharing_index, 100)}%` }}
              ></div>
            </div>
            
            <div className="text-xs text-gray-600">
              Based on mentor diversity and cross-department collaboration
            </div>
          </div>
        </div>
      </div>

      {/* Cross-Department Collaboration Details */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Cross-Department Collaboration</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-3">Collaboration Summary</h4>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Cross-department workshops:</span>
                <span className="text-sm font-semibold text-gray-900">
                  {crossDepartmentInsights.cross_department_workshops}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Departments involved:</span>
                <span className="text-sm font-semibold text-gray-900">
                  {crossDepartmentInsights.total_departments_involved}
                </span>
              </div>
            </div>
          </div>
          
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-3">Department Collaboration</h4>
            <div className="space-y-2 max-h-32 overflow-y-auto">
              {Object.entries(crossDepartmentInsights.department_collaboration).map(([dept, data]) => (
                <div key={dept} className="flex justify-between items-center">
                  <span className="text-sm text-gray-600 truncate">{dept}</span>
                  <div className="flex items-center space-x-2">
                    <span className="text-xs text-gray-500">{data.workshop_count} workshops</span>
                    <span className="text-xs text-gray-500">({data.mentors.length} mentors)</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Performance Insights */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Collaboration Performance</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-center w-12 h-12 bg-blue-100 rounded-full mx-auto mb-3">
              <Activity className="h-6 w-6 text-blue-600" />
            </div>
            <h4 className="text-sm font-medium text-gray-900 mb-1">Overall Collaboration</h4>
            <div className={`text-2xl font-bold ${getScoreColor(data.collaboration_score)}`}>
              {formatNumber(data.collaboration_score)}%
            </div>
            <div className="text-xs text-gray-600 mt-1">
              {getScoreBadge(data.collaboration_score).text}
            </div>
          </div>
          
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-center w-12 h-12 bg-green-100 rounded-full mx-auto mb-3">
              <BarChart3 className="h-6 w-6 text-green-600" />
            </div>
            <h4 className="text-sm font-medium text-gray-900 mb-1">Team Development</h4>
            <div className={`text-2xl font-bold ${getScoreColor(data.mentee_development_rate)}`}>
              {formatNumber(data.mentee_development_rate)}%
            </div>
            <div className="text-xs text-gray-600 mt-1">
              Mentee development rate
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
